import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { AlertBar } from "@/components/AlertBar";
import { Button } from "@/components/catalyst/Button";
import {
	ErrorMessage,
	Field,
	FieldGroup,
} from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { useTurnstile } from "@/hooks/use-turnstile";
import { client } from "@/orpc/client";
import { type Schema, schema } from "./schema";

export function Form() {
	const { getCaptchaHeaders, reset: resetTurnstile } = useTurnstile();
	const [submitted, setSubmitted] = useState(false);

	const {
		register,
		handleSubmit,
		setError,
		formState: { errors, isSubmitting },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
	});

	const { mutate: sendLink } = useMutation({
		mutationFn: async (data: Schema) => {
			return client.stripePartnerConnection.requestAccess(data, {
				context: { headers: getCaptchaHeaders() },
			});
		},
		onSuccess: () => setSubmitted(true),
		onError: () => {
			resetTurnstile();
			setError("root", {
				message: "Something went wrong. Please try again.",
			});
		},
	});

	const onSubmit = handleSubmit(async (data) => {
		sendLink(data);
	});

	if (submitted) {
		return (
			<div className="text-center space-y-4">
				<div className="text-4xl">📬</div>
				<p className="text-base-content/80">
					If an account exists for that email, we've sent a secure link to
					access your payout dashboard.
				</p>
				<p className="text-sm text-base-content/60">
					The link expires in 24 hours.
				</p>
			</div>
		);
	}

	return (
		<form className="space-y-6" onSubmit={onSubmit}>
			{errors.root?.message && (
				<AlertBar title="Woops!" variant="error">
					{errors.root?.message}
				</AlertBar>
			)}
			<FieldGroup>
				<Field>
					<Input type="email" {...register("email")} placeholder="Email" />
					{errors.email?.message && (
						<ErrorMessage>{errors.email.message}</ErrorMessage>
					)}
				</Field>
			</FieldGroup>

			<Button
				type="submit"
				color="dark/white"
				className="w-full"
				disabled={isSubmitting}
			>
				{isSubmitting ? "Sending..." : "Send secure link"}
			</Button>
		</form>
	);
}
